<template>
  <div class="container mx-auto px-4 py-8">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Updates</h1>
      <button
        @click="showAddModal = true"
        class="btn-primary"
      >
        Add Update
      </button>
    </div>

    <DataTable
      :columns="columns"
      resourceName="updates"
      @rowClick="handleRowClick"
    >
      <template #actions="{ item }">
        <button
          @click.stop="confirmDelete(item)"
          class="p-1 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
          title="Delete Update"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </template>
    </DataTable>

    <!-- Add/Edit Modal -->
    <div v-if="showAddModal || showEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" @click.self="showAddModal = false; showEditModal = false">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[calc(100vh-2rem)] overflow-hidden flex flex-col">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
            {{ showEditModal ? 'Edit Update' : 'Add Update' }}
          </h2>
        </div>
        <div class="p-6 overflow-y-auto flex-1">
          <form @submit.prevent="handleSubmit" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Title</label>
              <input
                v-model="form.title"
                type="text"
                required
                class="input mt-1"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
              <select
                v-model="form.category"
                required
                class="input mt-1"
              >
                <option value="">Select a category</option>
                <option value="Compliance">Compliance</option>
                <option value="Policy">Policy</option>
                <option value="Training">Training</option>
                <option value="Security">Security</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Content</label>
              <RichTextEditor
                v-model="form.content"
                placeholder="Enter update content..."
                class="mt-1"
              />
            </div>
          </form>
        </div>
        <div class="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 flex-shrink-0">
          <button
            type="button"
            @click="closeModal"
            class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <!-- Delete button (only show in edit mode) -->
          <button
            v-if="showEditModal"
            type="button"
            @click.stop="confirmDeleteFromModal"
            class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Delete
          </button>
          <button
            @click="handleSubmit"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {{ showEditModal ? 'Save Changes' : 'Add Update' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <DeleteConfirmationModal
      :show="showDeleteModal"
      title="Confirm Delete"
      message="Are you sure you want to delete this update? This action cannot be undone."
      :loading="isDeleting"
      @confirm="handleDelete"
      @cancel="showDeleteModal = false"
    />
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue'
import { useStore } from 'vuex'
import DataTable from '@/components/DataTable.vue'
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue'
import RichTextEditor from '@/components/RichTextEditor.vue'

export default {
  name: 'UpdatesView',
  components: {
    DataTable,
    DeleteConfirmationModal,
    RichTextEditor
  },
  setup() {
    const store = useStore()
    const showAddModal = ref(false)
    const showEditModal = ref(false)
    const showDeleteModal = ref(false)
    const isDeleting = ref(false)
    const selectedUpdate = ref(null)
    const form = ref({
      title: '',
      category: '',
      content: ''
    })

    const columns = [
      { key: 'id', label: 'ID' },
      { key: 'title', label: 'Title' },
      { key: 'category', label: 'Category' },
      { 
        key: 'content', 
        label: 'Content',
        formatter: (value) => {
          if (!value) return ''
          // Strip HTML tags for table display
          const temp = document.createElement('div')
          temp.innerHTML = value
          const textContent = temp.textContent || temp.innerText || ''
          return textContent.substring(0, 100) + (textContent.length > 100 ? '...' : '')
        }
      },
      { 
        key: 'created_at', 
        label: 'Created At',
        formatter: (value) => new Date(value).toLocaleDateString()
      },
      { 
        key: 'updated_at', 
        label: 'Updated At',
        formatter: (value) => new Date(value).toLocaleDateString()
      },
      { key: 'actions', label: 'Actions', slot: 'actions', class: 'w-16' }
    ]

    const currentTenantId = computed(() => store.state.currentTenantId)

    const refreshData = () => {
      if (currentTenantId.value) {
        store.dispatch('updates/fetchData', {
          tenantId: currentTenantId.value,
          page: 1,
          entriesPerPage: 10,
          search: '',
          orderBy: 'created_at',
          sortDirection: 'desc'
        })
      }
    }

    const handleRowClick = (update) => {
      console.log('handleRowClick called with:', update)
      selectedUpdate.value = update
      showEditModal.value = true
      form.value = {
        title: update.title,
        category: update.category,
        content: update.content
      }
    }

    const closeModal = () => {
      showAddModal.value = false
      showEditModal.value = false
      showDeleteModal.value = false
      selectedUpdate.value = null
      form.value = {
        title: '',
        category: '',
        content: ''
      }
    }

    const handleSubmit = async () => {
      try {
        const currentTenantId = store.state.currentTenantId
        if (!currentTenantId) {
          throw new Error('No tenant selected')
        }

        const updateData = {
          title: form.value.title,
          content: form.value.content,
          category: form.value.category,
          tenant_id: currentTenantId
        }

        if (showEditModal.value && selectedUpdate.value) {
          await store.dispatch('updates/updateData', {
            tenantId: currentTenantId,
            id: selectedUpdate.value.id,
            data: updateData
          })
        } else {
          await store.dispatch('updates/createData', {
            tenantId: currentTenantId,
            data: updateData
          })
        }

        closeModal()
        refreshData()
      } catch (error) {
        store.commit('setError', error.response?.data?.message || 'Failed to save update')
      }
    }

    const handleDelete = async () => {
      if (!selectedUpdate.value) return

      try {
        isDeleting.value = true
        const currentTenantId = store.state.currentTenantId
        if (!currentTenantId) {
          throw new Error('No tenant selected')
        }

        await store.dispatch('updates/deleteData', {
          tenantId: currentTenantId,
          id: selectedUpdate.value.id
        })

        closeModal()
        refreshData()
      } catch (error) {
        store.commit('setError', error.response?.data?.message || 'Failed to delete update')
      } finally {
        isDeleting.value = false
      }
    }

    const confirmDelete = (update) => {
      console.log('confirmDelete called with:', update)
      selectedUpdate.value = update
      showDeleteModal.value = true
    }

    const confirmDeleteFromModal = () => {
      console.log('confirmDeleteFromModal called')
      console.log('selectedUpdate.value:', selectedUpdate.value)
      
      if (!selectedUpdate.value) {
        console.error('No selectedUpdate available')
        return
      }
      
      showDeleteModal.value = true
    }

    onMounted(refreshData)

    watch(currentTenantId, (newTenantId) => {
      if (newTenantId) {
        refreshData()
      }
    })

    return {
      columns,
      showAddModal,
      showEditModal,
      showDeleteModal,
      isDeleting,
      form,
      handleRowClick,
      closeModal,
      handleSubmit,
      handleDelete,
      confirmDelete,
      confirmDeleteFromModal
    }
  }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.15s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>